import React from 'react';

export type LaunchStepStatus = 'pending' | 'active' | 'done' | 'error';

export interface LaunchStep {
  id: string;
  label: string;
  detail?: string;
  status: LaunchStepStatus;
}

export interface SimLaunchOverlayProps {
  open: boolean;
  title?: string;
  subtitle?: string;
  steps: LaunchStep[];
  logLines?: string[];
  error?: string | null;
  onDismissError?: () => void;
}

function stepIcon(status: LaunchStepStatus) {
  if (status === 'done') return '✓';
  if (status === 'error') return '!';
  if (status === 'active') return '…';
  return '·';
}

function stepColor(status: LaunchStepStatus) {
  if (status === 'done') return '#4ade80';
  if (status === 'error') return '#f87171';
  if (status === 'active') return '#fbbf24';
  return 'rgba(255,255,255,0.35)';
}

/**
 * Overlay fullscreen ao clicar em INICIAR SIMULAÇÃO — mostra o que está acontecendo.
 * Visual alinhado ao overlay de seed de escola (EscolaStep).
 */
export function SimLaunchOverlay({
  open,
  title = 'INICIANDO SIMULAÇÃO',
  subtitle = 'Preparando a sessão municipal e o cockpit. Aguarde…',
  steps,
  logLines = [],
  error,
  onDismissError,
}: SimLaunchOverlayProps) {
  if (!open) return null;

  const doneCount = steps.filter((s) => s.status === 'done').length;
  const pct = steps.length
    ? Math.round((doneCount / steps.length) * 100)
    : 0;
  const failed = Boolean(error) || steps.some((s) => s.status === 'error');

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(5, 2, 20, 0.92)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: '#fff',
        fontFamily: 'sans-serif',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <style>{`
        @keyframes simLaunchSpin { to { transform: rotate(360deg); } }
        @keyframes simLaunchPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.06); opacity: 0.85; }
        }
      `}</style>

      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: failed ? 'rgba(248,113,113,0.08)' : 'rgba(16,185,129,0.08)',
          border: `2px solid ${failed ? 'rgba(248,113,113,0.4)' : 'rgba(16,185,129,0.45)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 18,
          animation: failed ? 'none' : 'simLaunchPulse 2s ease-in-out infinite',
          boxShadow: failed
            ? '0 8px 32px rgba(248,113,113,0.15)'
            : '0 8px 32px rgba(16,185,129,0.2)',
        }}
      >
        {failed ? (
          <span style={{ fontSize: 32, fontWeight: 900, color: '#f87171' }}>!</span>
        ) : (
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '3px solid rgba(16,185,129,0.25)',
              borderTopColor: '#10b981',
              display: 'inline-block',
              animation: 'simLaunchSpin 0.75s linear infinite',
            }}
          />
        )}
      </div>

      <h2
        style={{
          fontSize: 20,
          fontWeight: 900,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          textAlign: 'center',
          margin: '0 0 8px 0',
          background: failed
            ? 'linear-gradient(135deg, #fca5a5, #f87171)'
            : 'linear-gradient(135deg, #6ee7b7 0%, #10b981 60%, #059669 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {failed ? 'FALHA AO INICIAR' : title}
      </h2>

      <p
        style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.7)',
          maxWidth: 460,
          textAlign: 'center',
          margin: '0 0 22px 0',
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        {failed ? (error || 'Não foi possível concluir a inicialização.') : subtitle}
      </p>

      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'rgba(255,255,255,0.05)',
          border: '1.2px solid rgba(255,255,255,0.1)',
          borderRadius: 12,
          padding: 4,
          marginBottom: 20,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: `${Math.max(pct, failed ? pct : 8)}%`,
            height: 14,
            background: failed
              ? 'linear-gradient(90deg, #f87171, #ef4444)'
              : 'linear-gradient(90deg, #10b981, #059669)',
            borderRadius: 8,
            transition: 'width 0.35s ease',
          }}
        />
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: 460,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 18,
        }}
      >
        {steps.map((s) => (
          <div
            key={s.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '10px 12px',
              borderRadius: 10,
              background:
                s.status === 'active'
                  ? 'rgba(251,191,36,0.08)'
                  : s.status === 'error'
                    ? 'rgba(248,113,113,0.08)'
                    : 'rgba(255,255,255,0.03)',
              border: `1px solid ${
                s.status === 'active'
                  ? 'rgba(251,191,36,0.3)'
                  : s.status === 'error'
                    ? 'rgba(248,113,113,0.3)'
                    : 'rgba(255,255,255,0.06)'
              }`,
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 900,
                color: stepColor(s.status),
                border: `1.5px solid ${stepColor(s.status)}`,
                background: 'rgba(0,0,0,0.25)',
              }}
            >
              {stepIcon(s.status)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: stepColor(s.status),
                  letterSpacing: '0.02em',
                }}
              >
                {s.label}
              </div>
              {s.detail && (
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.45)',
                    marginTop: 2,
                    lineHeight: 1.4,
                  }}
                >
                  {s.detail}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {logLines.length > 0 && (
        <div
          style={{
            width: '100%',
            maxWidth: 460,
            background: 'rgba(5, 2, 20, 0.94)',
            border: '1px solid rgba(16,185,129,0.25)',
            borderRadius: 10,
            padding: 12,
            boxSizing: 'border-box',
            fontFamily: 'monospace',
            fontSize: 9.5,
            lineHeight: 1.5,
            height: 110,
            overflowY: 'auto',
            textAlign: 'left',
            boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8)',
            marginBottom: failed ? 16 : 0,
          }}
        >
          {logLines.map((line, idx) => (
            <div
              key={idx}
              style={{
                color: line.startsWith('✅')
                  ? '#4ade80'
                  : line.startsWith('❌')
                    ? '#f87171'
                    : '#a7f3d0',
                marginBottom: 3,
              }}
            >
              {line}
            </div>
          ))}
        </div>
      )}

      {failed && onDismissError && (
        <button
          type="button"
          onClick={onDismissError}
          style={{
            marginTop: 8,
            padding: '12px 28px',
            borderRadius: 12,
            border: '1px solid rgba(248,113,113,0.4)',
            background: 'rgba(248,113,113,0.12)',
            color: '#fecaca',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Fechar e tentar novamente
        </button>
      )}
    </div>
  );
}
